import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  Facebook,
  Instagram,
  Check,
  ArrowRight,
  RefreshCw,
  Database,
  Shield,
  Activity,
  Zap,
  Pencil,
  AlertTriangle,
  TrendingDown,
  ShieldCheck,
  Clock,
  Headphones,
} from "lucide-react";
import { FOOTER_SOCIAL_LINKS } from "@/components/ui/SocialLinks";
import { HeroNav } from "@/components/ui/hero/HeroNav";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { HeroSocialProof } from "@/components/ui/hero/HeroSocialProof";
import { Footer } from "@/components/ui/footer";
import { ContactSection } from "@/components/sections/ContactSection";
import { AboutSection } from "@/components/sections/AboutSection";
import { WhyUsSection } from "@/components/sections/WhyUsSection";

import type { GrowthItem } from "@/components/sections/WhyUsSection.data";
import { cn } from "@/lib/utils";
import logo from "@/assets/mind-media-logo.svg";

export const Route = createFileRoute("/care-plans")({
  head: () => ({
    meta: [
      { name: "robots", content: "noindex, nofollow" },
      { title: "Website Care Plans — Mind Media" },
      {
        name: "description",
        content:
          "Updates, backups, security, and edits handled every month by the team that built your site. Predictable monthly cost, no panic invoices.",
      },
      { property: "og:title", content: "Website Care Plans — Mind Media" },
      {
        property: "og:description",
        content:
          "Your website, looked after. Updates, backups, security, and edits handled every month.",
      },
    ],
  }),
  component: CarePlansPage,
});

const gradText =
  "bg-gradient-to-r from-brand-blue via-purple-500 to-pink-500 bg-clip-text text-transparent";

const carePlanGrowthItems: GrowthItem[] = [
  {
    title: "Stay protected",
    description:
      "Daily backups, security scans, and a firewall watching your site around the clock.",
    Icon: ShieldCheck,
    bgClass: "bg-[#3b82f6]/10",
    textClass: "text-[#3b82f6]",
  },
  {
    title: "Fixes without the wait",
    description: "Small edits and updates handled each month, no more hunting for a freelancer.",
    Icon: Clock,
    bgClass: "bg-[#a855f7]/10",
    textClass: "text-[#a855f7]",
  },
  {
    title: "Help when you need it",
    description: "The same team that knows your site answers questions and jumps on issues fast.",
    Icon: Headphones,
    bgClass: "bg-[#ec4899]/10",
    textClass: "text-[#ec4899]",
  },
];

function CarePlansPage() {
  const navigate = useNavigate();
  const goToMockup = () => navigate({ to: "/get-mockup" });

  return (
    <main className="min-h-screen bg-background text-foreground">
      {/* HERO */}
      <section className="relative isolate overflow-hidden bg-background pb-4 text-center">
        <div
          aria-hidden
          className="pointer-events-none absolute -left-40 -bottom-40 sm:-left-56 sm:-bottom-56 h-[420px] w-[420px] sm:h-[720px] sm:w-[720px] rounded-full bg-hero-glow-warm opacity-35 blur-3xl sm:blur-[120px]"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute -right-40 -top-40 sm:-right-56 sm:-top-56 h-[420px] w-[420px] sm:h-[720px] sm:w-[720px] rounded-full bg-hero-glow-cool opacity-40 blur-3xl sm:blur-[120px]"
        />
        <HeroNav
          logo={<img src={logo} alt="Mind Media" className="h-7 w-auto" />}
          navigation={[
            { label: "Work", href: "/#work" },
            { label: "About", href: "#about" },
            { label: "Services", href: "/#pricing" },
            { label: "Pricing", href: "#pricing" },
            { label: "FAQ", href: "#faq" },
            { label: "Contact", href: "#contact" },
          ]}
          ctaButton={{ label: "Book a Meeting", href: "#contact" }}
          mobilePricingHref="#pricing"
        />
        <div className="relative mx-auto px-6 mt-12 max-w-4xl -translate-y-5 text-left sm:text-center sm:mt-16 lg:max-w-none">
          <HeroSocialProof mobileAlign="left" />
          <h1 className="mx-auto max-w-3xl text-left text-4xl sm:text-center font-bold tracking-tight text-foreground sm:text-5xl lg:max-w-none lg:whitespace-nowrap lg:text-5xl">
            Your website, <span className={gradText}>looked after.</span>
          </h1>
          <p className="mx-auto mt-5 max-w-5xl text-lg text-muted-foreground sm:text-xl">
            Updates, backups, security, and edits handled every month by the team that knows your
            site.
            <br className="hidden sm:block" /> So it stays fast, safe, and online while you get on
            with running your business.
          </p>
          <div className="mt-8 flex flex-wrap justify-start gap-3 sm:justify-center">
            <a
              href="#pricing"
              className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-brand-blue to-[#ec4899] px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-brand-blue/40 transition hover:-translate-y-0.5 hover:opacity-90"
            >
              See care plans <ArrowRight className="h-4 w-4" />
            </a>
            <a
              href="#includes"
              className="inline-flex items-center gap-2 rounded-full border border-border bg-background px-6 py-3 text-sm font-semibold text-foreground transition hover:-translate-y-0.5 hover:bg-accent"
            >
              What's covered
            </a>
          </div>
          <div className="relative mx-auto mt-8 flex flex-wrap justify-start gap-x-16 sm:justify-center gap-y-4">
            {["Daily backups", "Security monitoring", "Edits handled for you"].map((p) => (
              <span key={p} className="inline-flex items-center gap-2 text-base font-medium">
                <span className="flex h-5 w-5 items-center justify-center rounded-full border border-brand-green bg-white">
                  <Check className="h-3 w-3 text-brand-green" strokeWidth={3} />
                </span>
                {p}
              </span>
            ))}
          </div>
        </div>
        <WhyUsSection
          onCtaClick={goToMockup}
          variant="hero"
          showTrustBar={false}
          showRealExperience={false}
          withDivider={false}
          shareloId="bN3ZEA3c5X0pYXdS4n61f"
          shareloTitle="Care plans — customer story"
          headline={
            <>
              <span className="block sm:inline">What a care plan</span>{" "}
              <span className="block sm:inline">
                <span className="inline-flex items-baseline whitespace-nowrap sm:inline">
                  <span className="text-gradient-cycle align-baseline">does for you</span>
                </span>
              </span>
            </>
          }
          growthItems={carePlanGrowthItems}
        />
      </section>

      {/* PROBLEM */}
      <section className="bg-muted/30 px-6 py-20">
        <div className="mx-auto max-w-6xl">
          <SectionHead
            kickerWeight="font-bold"
            kicker="When nobody's watching"
            maxWidth="max-w-6xl lg:max-w-none"
            titleClassName="lg:whitespace-nowrap"
            childrenClassName="lg:whitespace-nowrap"
            title={
              <>
                A website left alone <span className={gradText}>quietly falls apart.</span>
              </>
            }
          >
            Sites aren't "set it and forget it." Leave one unattended and the problems show up at
            the worst possible time.
          </SectionHead>
          <div className="grid gap-6 md:grid-cols-3">
            {[
              {
                icon: AlertTriangle,
                n: "An update",
                h: "Breaks the site",
                p: "A plugin update goes sideways and your site goes down, usually on a busy day when you least expect it.",
              },
              {
                icon: Shield,
                n: "Hackers",
                h: "Find the gap",
                p: "Out-of-date software is the number one way small sites get hacked. Patching has to happen constantly, not once a year.",
              },
              {
                icon: TrendingDown,
                n: "It rots",
                h: "A little at a time",
                p: "Slow load times, broken forms, dead links. Small issues pile up until one day the site barely works.",
              },
            ].map(({ icon: Icon, ...c }) => (
              <div key={c.h} className="rounded-2xl border border-border bg-card p-7 shadow-sm">
                <div className="flex items-center gap-4">
                  <div className="inline-flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-brand-blue/15 to-pink-500/15">
                    <Icon className="h-5 w-5 text-brand-blue" strokeWidth={2.25} />
                  </div>
                  <div>
                    <div
                      className={cn(
                        "text-3xl tracking-tight md:text-4xl leading-none font-bold text-brand-blue",
                      )}
                    >
                      {c.n}
                    </div>
                    <h3 className="mt-0.5 text-lg font-bold tracking-tight leading-tight">{c.h}</h3>
                  </div>
                </div>
                <p className="mt-4 text-[15px] text-muted-foreground">{c.p}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT'S COVERED */}
      <section id="includes" className="px-6 py-20">
        <div className="mx-auto max-w-6xl">
          <SectionHead
            maxWidth="max-w-4xl"
            kickerWeight="font-bold"
            kicker="What's covered"
            title={
              <>
                Everything that keeps a site <span className={gradText}>healthy.</span>
              </>
            }
          >
            Handled quietly in the background so you never have to think about it, or scramble when
            something breaks.
          </SectionHead>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {[
              {
                icon: RefreshCw,
                h: "Updates",
                p: "WordPress core, plugins, and themes kept current and tested, so updates fix things instead of breaking them.",
              },
              {
                icon: Database,
                h: "Daily backups",
                p: "Automatic offsite backups every day, so whatever happens, we can roll your site back in minutes.",
              },
              {
                icon: Shield,
                h: "Security monitoring",
                p: "Malware scans and a firewall watching around the clock, catching threats before they become a problem.",
              },
              {
                icon: Activity,
                h: "Uptime monitoring",
                p: "We watch your site 24/7 and know it went down before you do, so it's back up fast.",
              },
              {
                icon: Zap,
                h: "Performance",
                p: "Caching and regular speed checks so your site stays fast as it grows, instead of slowly bogging down.",
              },
              {
                icon: Pencil,
                h: "Content edits",
                p: "A set amount of small edits each month, swap an image, update a price, add a page, handled for you.",
              },
            ].map(({ icon: Icon, h, p }) => (
              <div
                key={h}
                className="group rounded-2xl border border-border bg-card p-7 transition hover:-translate-y-1 hover:border-brand-blue/30 hover:shadow-xl hover:shadow-brand-blue/10"
              >
                <div className="mb-4 flex items-center gap-3">
                  <div className="inline-flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-brand-blue/15 to-pink-500/15">
                    <Icon className="h-5 w-5 text-brand-blue" strokeWidth={2.25} />
                  </div>
                  <h3 className="text-lg font-bold tracking-tight">{h}</h3>
                </div>
                <p className="text-[15px] text-muted-foreground">{p}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="bg-muted/30 px-6 py-20">
        <div className="mx-auto max-w-6xl">
          <SectionHead
            maxWidth="max-w-6xl"
            kickerWeight="font-bold"
            kicker="Why us"
            title={
              <span className="lg:whitespace-nowrap">
                The people who built it <span className={gradText}>keep it running.</span>
              </span>
            }
          >
            When something breaks, you're not hunting for a freelancer who's never seen your site.
            It's the same team that built it, already knows it, and can fix it fast. Predictable
            monthly cost instead of panic and emergency fees.
          </SectionHead>
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <ul className="space-y-5">
              {[
                [
                  "Same team, every time",
                  "No re-explaining your site to a stranger when it matters most.",
                ],
                [
                  "We catch it first",
                  "Monitoring means we often fix problems before you even notice.",
                ],
                [
                  "No surprise emergency bills",
                  "One predictable monthly cost instead of a panic invoice when things go wrong.",
                ],
                [
                  "It's always your site",
                  "You own everything. Leave whenever you want, no hostage situation.",
                ],
              ].map(([title, body]) => (
                <li key={title} className="flex gap-4">
                  <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-brand-blue/15 to-pink-500/15">
                    <Check className="h-4 w-4 text-brand-blue" strokeWidth={3} />
                  </span>
                  <div>
                    <strong className="block text-base font-bold">{title}</strong>
                    <span className="text-[15px] text-muted-foreground">{body}</span>
                  </div>
                </li>
              ))}
            </ul>
            <div className="relative rounded-3xl bg-gradient-to-br from-brand-blue via-purple-600 to-pink-500 p-10 text-white shadow-2xl shadow-purple-500/30">
              <div className="absolute right-6 top-6 text-right">
                <div className="text-3xl font-bold leading-none">20+</div>
                <div className="text-xs font-medium uppercase tracking-wider text-white/80">
                  YEARS EXP
                </div>
              </div>
              <div className="text-6xl tracking-tight leading-none font-bold text-white">24/7</div>
              <div className="mt-2 text-lg font-bold">while you sleep, we're watching</div>
              <p className="mt-5 text-[15px] leading-relaxed text-white/90">
                Backups, security scans, and uptime checks run around the clock, every day. So if
                something goes wrong at 3am, it gets caught and handled before it costs you a single
                customer, instead of you finding out from an angry email the next morning.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section id="process" className="px-6 pt-20 pb-10">
        <div className="mx-auto max-w-6xl">
          <SectionHead
            maxWidth="max-w-6xl"
            kickerWeight="font-bold"
            kicker="How it works"
            title={
              <span className="lg:whitespace-nowrap">
                Set it up once, then <span className={gradText}>stop worrying.</span>
              </span>
            }
          />
          <div className="grid gap-6 md:grid-cols-3">
            {[
              {
                n: 1,
                h: "Onboard your site",
                p: "We set up backups, security, and monitoring, and do a first health check to catch anything already lurking.",
              },
              {
                n: 2,
                h: "We maintain it",
                p: "Updates, scans, and performance checks happen on schedule in the background. You don't lift a finger.",
              },
              {
                n: 3,
                h: "You stay in the loop",
                p: "A simple monthly report tells you what we did, and you send over any edits you need handled.",
              },
            ].map((s) => (
              <div
                key={s.n}
                className="flex items-center gap-5 rounded-2xl border border-border bg-card p-6"
              >
                <div
                  className={cn(
                    "flex-shrink-0 text-6xl leading-none tracking-tight font-bold text-brand-blue",
                  )}
                >
                  {s.n}
                </div>
                <div>
                  <h3 className="text-base font-bold tracking-tight">{s.h}</h3>
                  <p className="mt-2 text-sm text-muted-foreground">{s.p}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ABOUT */}
      <AboutSection
        className="pt-2 pb-6 sm:pt-4 sm:pb-8"
        contentClassName="mt-2 sm:mt-4"
        hideKicker
        hideMockupButton
        meetingButtonPrimary
        centerContent
      />

      {/* PRICING */}
      <section id="pricing" className="bg-muted/30 px-6 py-20">
        <div className="mx-auto max-w-6xl">
          <SectionHead
            kickerWeight="font-bold"
            kicker="Plans"
            maxWidth="max-w-5xl"
            titleClassName="lg:whitespace-nowrap"
            title={
              <>
                Peace of mind, <span className={gradText}>monthly.</span>
              </>
            }
          >
            Simple monthly plans. No long contracts, cancel anytime.
          </SectionHead>
          <div className="grid items-stretch gap-6 md:grid-cols-3">
            {[
              {
                h: "Essential",
                desc: "Keep it safe and current",
                amt: "$99",
                sub: "The basics, covered",
                b: [
                  "Core, plugin & theme updates",
                  "Daily backups",
                  "Security monitoring",
                  "30 min of edits / month",
                ],
                cta: "ghost",
                feat: false,
              },
              {
                h: "Pro",
                desc: "For sites that matter to the business",
                amt: "$249",
                sub: "Protection plus a real edit allowance",
                b: [
                  "Everything in Essential",
                  "Uptime monitoring",
                  "Performance optimization",
                  "1 hour of edits / month",
                ],
                cta: "primary",
                feat: true,
              },
              {
                h: "Premium",
                desc: "For stores & busy sites",
                amt: "$499",
                sub: "Top priority, more hands-on",
                b: [
                  "Everything in Pro",
                  "Priority response",
                  "3 hours of edits / month",
                  "Monthly check-in call",
                ],
                cta: "ghost",
                feat: false,
              },
            ].map((p) => (
              <div
                key={p.h}
                className={cn(
                  "relative flex flex-col rounded-2xl border bg-card p-8",
                  p.feat
                    ? "border-transparent shadow-2xl shadow-purple-500/20 ring-2 ring-transparent [background:linear-gradient(var(--card),var(--card))_padding-box,linear-gradient(100deg,oklch(0.585_0.196_263),#a855f7,#ec4899)_border-box]"
                    : "border-border",
                )}
              >
                {p.feat && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-gradient-to-r from-brand-blue via-purple-500 to-pink-500 px-4 py-1.5 text-[11px] font-extrabold uppercase tracking-wider text-white">
                    Most popular
                  </span>
                )}
                <h3 className="text-lg font-bold tracking-tight">{p.h}</h3>
                <div className="mt-1 text-sm text-muted-foreground">{p.desc}</div>
                <div className="mt-5 text-4xl font-extrabold tracking-tight">
                  {p.amt}
                  <small className="ml-1 text-sm font-semibold text-muted-foreground">/mo</small>
                </div>
                <div className="mt-1 text-xs font-medium text-muted-foreground">{p.sub}</div>
                <ul className="mt-6 flex-1 space-y-3">
                  {p.b.map((li) => (
                    <li key={li} className="flex items-start gap-2 text-sm font-medium">
                      <Check
                        className="mt-0.5 h-4 w-4 flex-shrink-0 text-brand-green"
                        strokeWidth={3}
                      />
                      {li}
                    </li>
                  ))}
                </ul>
                <button
                  onClick={goToMockup}
                  className={cn(
                    "mt-6 inline-flex w-full items-center justify-center rounded-full px-5 py-3 text-sm font-bold transition hover:-translate-y-0.5",
                    p.cta === "primary"
                      ? "bg-gradient-to-r from-brand-blue to-[#ec4899] text-white shadow-lg shadow-brand-blue/40 hover:opacity-90"
                      : "border-2 border-brand-blue bg-background text-brand-blue hover:bg-brand-blue/10",
                  )}
                >
                  Get started
                </button>
              </div>
            ))}
          </div>
          <p className="mt-6 text-center text-sm text-muted-foreground">
            No long contracts. Plans are month to month and you can change or cancel anytime.
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="px-6 py-20">
        <div className="mx-auto max-w-5xl">
          <SectionHead
            kickerWeight="font-bold"
            kicker="Questions"
            title={
              <>
                The stuff people <span className={gradText}>ask first.</span>
              </>
            }
          />
          {(() => {
            const items: [string, string][] = [
              [
                "Do I really need this?",
                "If your website matters to your business, yes. Sites need updates, backups, and security the same way a car needs oil changes. Skip it and you usually find out the hard way.",
              ],
              [
                "What if my site goes down?",
                "We're monitoring it, so we often know before you do. We jump on it fast, and because we keep daily backups, we can roll back to a working version in minutes if needed.",
              ],
              [
                "How many edits do I get?",
                "It depends on your plan, from 30 minutes up to a few hours of small edits each month. Bigger projects we'll quote separately, but day-to-day tweaks are covered.",
              ],
              [
                "Is hosting included?",
                "Care plans cover maintenance, not hosting itself, though we work with your host and can help you pick a good one. If you'd like us to manage hosting too, just ask.",
              ],
              [
                "Can I cancel anytime?",
                "Yes. Plans are month to month with no long contracts. You stay because it's worth it, and everything stays yours if you go.",
              ],
              [
                "Do you take over a site you didn't build?",
                "Usually, yes. We'll do a quick health check first to see what shape it's in and flag anything that needs fixing before the plan starts.",
              ],
            ];
            const col1 = items.filter((_, i) => i % 2 === 0);
            const col2 = items.filter((_, i) => i % 2 === 1);
            return (
              <div className="mx-auto mt-12 grid max-w-6xl gap-x-12 gap-y-2 md:grid-cols-2">
                <FaqColumn items={col1} />
                <FaqColumn items={col2} />
              </div>
            );
          })()}
        </div>
      </section>

      <ContactSection
        compact
        heading={
          <h2 className="text-5xl font-bold leading-[1.05] tracking-tight text-foreground sm:text-6xl">
            Stop worrying{" "}
            <span className="whitespace-nowrap">
              about{" "}
              <span className="bg-gradient-to-r from-brand-blue to-[#ec4899] bg-clip-text text-transparent">
                your website.
              </span>
            </span>
          </h2>
        }
        subheading={
          <p className="mt-6 text-lg text-muted-foreground">
            Updates, backups, security, and edits handled every month by the team that knows your
            site - So you focus on your business.
          </p>
        }
        ctaText="Get protected"
      />

      <Footer
        logo={<img src={logo} alt="Mind Media" className="h-7 w-auto" />}
        brandName="Mind Media"
        socialLinks={FOOTER_SOCIAL_LINKS}
        mainLinks={[
          { href: "/", label: "Home" },
          { href: "/care-plans", label: "Care Plans" },
          { href: "/seo", label: "SEO" },
          { href: "/#pricing", label: "Pricing" },
          { href: "#faq", label: "FAQ" },
          { href: "/blog", label: "Blog" },
          { href: "/services", label: "Services" },
        ]}
        legalLinks={[
          { href: "/privacy", label: "Privacy" },
          { href: "/terms", label: "Terms" },
        ]}
        copyright={{
          text: `© ${new Date().getFullYear()} Mind Media`,
          license: "All rights reserved",
        }}
      />
    </main>
  );
}

function Kicker({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <span
      className={cn(
        "inline-block rounded-full bg-brand-blue/10 px-4 py-1.5 text-xs uppercase tracking-[0.12em] text-brand-blue",
        className,
      )}
    >
      {children}
    </span>
  );
}

function SectionHead({
  kicker,
  title,
  children,
  kickerWeight = "font-bold",
  maxWidth = "max-w-2xl",
  titleClassName,
  childrenClassName,
}: {
  kicker: string;
  title: React.ReactNode;
  children?: React.ReactNode;
  kickerWeight?: "font-bold" | "font-medium";
  maxWidth?: string;
  titleClassName?: string;
  childrenClassName?: string;
}) {
  return (
    <div className={cn("mx-auto mb-12 text-center", maxWidth)}>
      <Kicker className={kickerWeight}>{kicker}</Kicker>
      <h2
        className={cn(
          "mt-4 text-4xl font-bold tracking-tight text-foreground sm:text-5xl lg:text-5xl",
          titleClassName,
        )}
      >
        {title}
      </h2>
      {children && (
        <p className={cn("mt-4 text-lg leading-relaxed text-muted-foreground", childrenClassName)}>
          {children}
        </p>
      )}
    </div>
  );
}

function FaqColumn({ items }: { items: [string, string][] }) {
  return (
    <Accordion type="single" collapsible className="w-full">
      {items.map(([q, a]) => (
        <AccordionItem key={q} value={q} className="border-b">
          <AccordionTrigger className="text-left text-base font-medium text-gray-950 md:text-lg">
            {q}
          </AccordionTrigger>
          <AccordionContent>
            <p className="text-muted-foreground text-base">{a}</p>
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
}
